/**
 * Renewing the session the admin's tabs share.
 *
 * Every tab reads the one refresh token in storage. When another tab traded it
 * a moment ago, the API answers REFRESH_CONFLICT rather than treating this tab
 * as a thief — and that tab's new token is about to land in storage. So wait
 * for it and trade that one, instead of clearing a session that is fine.
 */
export type Renewal = 'renewed' | 'lost' | 'pending';

export interface RenewDeps {
  stored: () => string | null;
  refresh: (refreshToken: string) => Promise<{ accessToken: string; refreshToken: string }>;
  keep: (tokens: { accessToken: string; refreshToken: string }) => void;
  isConflict: (error: unknown) => boolean;
  wait: (ms: number) => Promise<void>;
}

const ATTEMPTS = 3;
const WAIT_MS = 400;

export async function renew(deps: RenewDeps): Promise<Renewal> {
  for (let attempt = 0; attempt < ATTEMPTS; attempt += 1) {
    const token = deps.stored();
    if (!token) return 'lost';

    try {
      deps.keep(await deps.refresh(token));
      return 'renewed';
    } catch (error) {
      if (!deps.isConflict(error)) return 'lost';
      await deps.wait(WAIT_MS);
    }
  }
  // Still being renewed elsewhere: keep what is stored; the next call tries again.
  return 'pending';
}
